import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export const DELETE_ACC_TIDS = {
    openBtn: "delete-account-open-btn",
    dialog: "delete-account-dialog",
    passwordInput: "delete-account-password-input",
    confirmInput: "delete-account-confirm-input",
    reasonInput: "delete-account-reason-input",
    cancelBtn: "delete-account-cancel-btn",
    submitBtn: "delete-account-submit-btn",
};

/**
 * User-self-serve account deletion.
 * - Requires current password
 * - Requires typing the word DELETE (case-sensitive)
 * - Optional reason
 * - On success: logs the user out and redirects to landing with a farewell toast.
 */
export default function DeleteAccountDialog() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [reason, setReason] = useState("");

    const disabled = busy || password.length === 0 || confirm !== "DELETE";

    async function submit(e) {
        e.preventDefault();
        if (confirm !== "DELETE") return toast.error('Type "DELETE" to confirm');
        setBusy(true);
        try {
            const res = await api.post("/auth/delete-account", {
                current_password: password,
                confirm,
                reason: reason || null,
            });
            const openCr = res.data?.receipt?.open_care_requests_touched ?? 0;
            const pms = res.data?.receipt?.stripe_pms_detached ?? 0;
            toast.success(
                `Your account has been deleted.${pms ? ` ${pms} card${pms > 1 ? "s" : ""} detached.` : ""}${openCr ? ` ${openCr} open request${openCr > 1 ? "s" : ""} cancelled.` : ""}`,
                { duration: 6000 }
            );
            setOpen(false);
            logout();
            navigate("/", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <Button
                type="button"
                variant="outline"
                data-testid={DELETE_ACC_TIDS.openBtn}
                onClick={() => setOpen(true)}
                className="rounded-full border-red-200 bg-white text-red-700 hover:bg-red-50 hover:text-red-800 hover:border-red-300 font-outfit"
            >
                <Trash2 className="w-4 h-4 mr-2" /> Delete my account
            </Button>

            <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
                <DialogContent data-testid={DELETE_ACC_TIDS.dialog} className="max-w-lg rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-outfit text-red-700">
                            <AlertTriangle className="w-5 h-5" /> Delete your account
                        </DialogTitle>
                        <DialogDescription className="text-stone-600 font-dmsans">
                            This action is <strong>permanent</strong>. Once submitted:
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-1.5 text-xs font-dmsans text-red-900">
                        <div className="flex items-start gap-2">
                            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Your name, email, phone and address will be anonymised.</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Saved cards will be detached from Stripe.</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{user?.role === "family" ? "Your open care requests will be cancelled." : user?.role === "caregiver" ? "Your matched shifts will be released back to the pool." : "Your open assignments will be released."}</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Historical charges + tax records are <strong>kept</strong> for legal compliance (7 years) but no longer linked to your identity.</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>You <strong>cannot log back in</strong>. Registration with the same email will require a new account.</span>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-3 mt-2">
                        <div>
                            <Label htmlFor="del-pw" className="text-xs font-outfit font-semibold">Confirm your password</Label>
                            <Input
                                id="del-pw"
                                type="password"
                                autoComplete="current-password"
                                data-testid={DELETE_ACC_TIDS.passwordInput}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Your current password"
                                className="h-11 rounded-xl mt-1"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="del-confirm" className="text-xs font-outfit font-semibold">
                                Type <span className="font-mono text-red-700">DELETE</span> to confirm
                            </Label>
                            <Input
                                id="del-confirm"
                                data-testid={DELETE_ACC_TIDS.confirmInput}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                placeholder="DELETE"
                                className="h-11 rounded-xl mt-1 font-mono"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="del-reason" className="text-xs font-outfit font-semibold">Why are you leaving? <span className="text-stone-500 font-normal">(optional, helps us improve)</span></Label>
                            <textarea
                                id="del-reason"
                                data-testid={DELETE_ACC_TIDS.reasonInput}
                                value={reason}
                                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                                placeholder="Anything you'd like to share…"
                                rows={3}
                                className="w-full rounded-xl mt-1 border border-stone-200 p-3 text-sm font-dmsans focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none resize-none"
                            />
                        </div>

                        <DialogFooter className="mt-2 flex-row gap-2 sm:justify-between">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setOpen(false)}
                                disabled={busy}
                                data-testid={DELETE_ACC_TIDS.cancelBtn}
                                className="rounded-full flex-1 sm:flex-none"
                            >Cancel</Button>
                            <Button
                                type="submit"
                                disabled={disabled}
                                data-testid={DELETE_ACC_TIDS.submitBtn}
                                className="rounded-full flex-1 sm:flex-none bg-red-700 hover:bg-red-800 text-white font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {busy ? "Deleting…" : "Delete my account permanently"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
